/**
 * Compose Message Modal
 * Allows members to initiate new messages to their case managers
 */

import { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Send, CheckCircle } from 'lucide-react';
import { getMemberToken } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
interface ComposeMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ComposeMessageModal({ isOpen, onClose, onSuccess }: ComposeMessageModalProps) {
  const { t } = useAppTranslation();
  const [recipientType, setRecipientType] = useState('case_manager');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [urgency, setUrgency] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const token = getMemberToken();
      if (!token) {
        throw new Error('Please log in to send messages');
      }

      const endpoint = getWeaveinApiUrl('/api/member-portal/communications/send');

      const response = await authFetch(endpoint, {
        method: 'POST',
        jsonBody: {
          recipient_type: recipientType,
          subject: subject,
          message_body: message,
          urgency_level: urgency,
          requires_response: urgency === 'high'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        if (response.status === 404) {
          throw new Error('Messaging service not available. Please try again later.');
        }
        throw new Error(`Failed to send message: ${response.status}`);
      }

      // Success
      setSubmitted(true);

      // Reset form
      setTimeout(() => {
        setSubject('');
        setMessage('');
        setUrgency('normal');
        setRecipientType('case_manager');
        setSubmitted(false);
        onClose();
        if (onSuccess) onSuccess();
      }, 2000);

    } catch (err) {
      console.error('Error sending message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSubject('');
      setMessage('');
      setError('');
      setSubmitted(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            {submitted ? 'Message Sent!' : 'New Message'}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {t('member.message_sent_successfully', 'Message Sent Successfully!')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('member.your_case_manager_will_review', 'Your case manager will review your message and respond soon.')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Recipient Type */}
            <div>
              <Label htmlFor="recipient-type">{t('member.send_to', 'Send To')}</Label>
              <Select value={recipientType} onValueChange={setRecipientType}>
                <SelectTrigger id="recipient-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="case_manager">{t('member.my_case_manager', 'My Case Manager')}</SelectItem>
                  <SelectItem value="organization">{t('member.my_organization', 'My Organization')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {recipientType === 'case_manager'
                  ? 'Your assigned case manager will receive this message'
                  : 'Your organization support team will receive this message'}
              </p>
            </div>

            {/* Subject */}
            <div>
              <Label htmlFor="subject">{t('member.subject', 'Subject *')}</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('member.what_is_this_message_about', 'What is this message about?')}
                required
                maxLength={200}
              />
            </div>

            {/* Urgency */}
            <div>
              <Label htmlFor="urgency">{t('member.priority_level', 'Priority Level')}</Label>
              <Select value={urgency} onValueChange={setUrgency}>
                <SelectTrigger id="urgency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t('member.low_no_rush', 'Low - No rush')}</SelectItem>
                  <SelectItem value="normal">{t('member.normal_regular_followup', 'Normal - Regular follow-up')}</SelectItem>
                  <SelectItem value="high">{t('member.high_needs_quick_response', 'High - Needs quick response')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="message-body">{t('member.message', 'Message *')}</Label>
              <Textarea
                id="message-body"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('member.type_your_message_here', 'Type your message here...')}
                rows={8}
                required
                maxLength={2000}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {message.length}/2000 characters
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Info */}
            <div className="text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
              <strong>{t('member.note', 'Note:')}</strong> {t('member.you_can_expect_a_response', 'You can expect a response within 1-2 business days. For urgent matters, please call your case manager directly.')}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={submitting}
              >
                {t('member.cancel', 'Cancel')}
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-v2accent hover:bg-v2accent/90 dark:bg-teal-500 dark:hover:bg-teal-600"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    {t('member.sending', 'Sending...')}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t('member.send_message', 'Send Message')}
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
